import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ToastService } from '@cnt-workspace/data-access';

/**
 * Renders cnt-landing's ToastService queue (used by AuthService and the
 * admin screens). cnt-landing's own ToastHostComponent relies on its Tailwind
 * theme, which the admin app doesn't load, so this is a plain-CSS equivalent
 * of its look: a white card with a tone-coloured border and dot.
 */
@Component({
  selector: 'admin-toast-host',
  standalone: true,
  imports: [AsyncPipe],
  template: `
    <div class="toast-stack" aria-live="polite">
      @for (toast of toasts.toasts$ | async; track toast.id) {
        <!-- Errors interrupt the screen reader; anything else waits its turn. -->
        <div
          class="toast"
          [class]="'toast toast-' + toast.tone"
          [attr.role]="toast.tone === 'error' ? 'alert' : 'status'"
        >
          <span>{{ toast.message }}</span>
          @if (toast.actionLabel) {
            <button type="button" (click)="toast.action?.(); toasts.dismiss(toast.id)">{{ toast.actionLabel }}</button>
          }
          <button type="button" aria-label="Dismiss" (click)="toasts.dismiss(toast.id)">×</button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .toast-stack {
        position: fixed;
        right: 16px;
        bottom: 16px;
        z-index: 100000;
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-width: min(420px, calc(100vw - 32px));
      }
      .toast {
        --tone: var(--cnt-ink, #222);
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 14px 12px 16px;
        border: 1px solid rgba(34, 34, 34, 0.15);
        border-radius: 1rem;
        background: #fff;
        color: var(--cnt-ink, #222);
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
        font-size: 0.875rem;
        line-height: 1.4;
      }
      .toast::before {
        content: '';
        flex: none;
        width: 8px;
        height: 8px;
        border-radius: 9999px;
        background: var(--tone);
      }
      .toast span {
        flex: 1;
      }
      .toast-success {
        --tone: var(--cnt-green, #295d42);
        border-color: rgba(41, 93, 66, 0.3);
      }
      .toast-error {
        --tone: var(--cnt-danger, #b3261e);
        border-color: rgba(179, 38, 30, 0.3);
      }
      .toast-warn {
        --tone: #b3760e;
        border-color: rgba(251, 215, 132, 0.8);
      }
      .toast-info {
        --tone: var(--cnt-muted, #707070);
      }
      button {
        background: transparent;
        border: 0;
        color: var(--cnt-trinidad, #e3530d);
        font-family: var(--cnt-font-button, sans-serif);
        font-size: 0.75rem;
        font-weight: 700;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        cursor: pointer;
      }
      button[aria-label='Dismiss'] {
        color: var(--cnt-muted, #707070);
        font-size: 1.125rem;
        letter-spacing: 0;
      }
    `,
  ],
})
export class AdminToastHostComponent {
  readonly toasts = inject(ToastService);
}
